import { Component, OnInit, ChangeDetectorRef } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AuthService } from '../../../services/auth.service';
import { AuthResponse } from '../../../interfaces/auth.interface';
import { InsuranceCompany } from '../../../interfaces/insurance-company.interface';
import { Product, NewProduct } from '../../../interfaces/product.interface';
import { ProductDialog, ProductDialogData, ProductDialogResult } from './product-dialog/product-dialog.component';
import { EditIconButton } from '../../../shared/components/edit-icon-button/edit-icon-button';
import { AddNewButton } from '../../../shared/components/add-new-button/add-new-button';

@Component({
  selector: 'app-admin-products',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatSnackBarModule,
    MatDialogModule,
    EditIconButton,
    AddNewButton
],
  templateUrl: './admin-products.component.html',
  styleUrls: ['./admin-products.component.scss']
})
export class AdminProductsComponent implements OnInit {
  insuranceCompanies: InsuranceCompany[] = [];
  selectedInsuranceCompanyId: number | null = null;
  products: Product[] = [];

  constructor(
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.loadInsuranceCompanies();
  }

  private showNotification(message: string, type: 'success' | 'error' | 'info' = 'info') {
    const panelClass = type === 'success' ? 'success-snackbar' : 
                      type === 'error' ? 'error-snackbar' : 
                      'info-snackbar';
    
    this.snackBar.open(message, 'Bezárás', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: [panelClass]
    });
  }

  private loadInsuranceCompanies() {
    this.authService.getAllInsuranceCompanies().subscribe({
      next: (insuranceCompanies) => {
        this.insuranceCompanies = insuranceCompanies;
        if (insuranceCompanies.length > 0) {
          this.selectedInsuranceCompanyId = insuranceCompanies[0].id;
          this.loadProductsForCompany(insuranceCompanies[0].id);
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Hiba a biztosító társaságok betöltése során:', error);
        this.showNotification('Hiba történt a biztosító társaságok betöltése során', 'error');
      }
    });
  }

  openProductForm() {
    const dialogData: ProductDialogData = {
      formData: {
        insurancecompanyid: this.selectedInsuranceCompanyId || (this.insuranceCompanies[0]?.id ?? 0),
        productcode: '',
        productname: '',
        producttype: ''
      },
      insuranceCompanies: this.insuranceCompanies,
      isSubmitting: false
    };

    const dialogRef = this.dialog.open(ProductDialog, {
      width: '500px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: ProductDialogResult) => {
      if (result?.action === 'save' && result.data) {
        this.saveProduct(result.data);
      }
    });
  }

  private saveProduct(productData: NewProduct) {
    this.authService.createProduct(productData).subscribe({
      next: (response: AuthResponse) => {
        this.showNotification('Termék sikeresen hozzáadva!', 'success');
        // Frissítjük a termékek listáját
        if (this.selectedInsuranceCompanyId) {
          this.loadProductsForCompany(this.selectedInsuranceCompanyId);
        }
      },
      error: (error: any) => {
        console.error('Hiba a termék mentésekor:', error);
        this.showNotification('Hiba történt a termék mentése során', 'error');
      }
    });
  }

  selectInsuranceCompany(companyId: number) {
    this.selectedInsuranceCompanyId = companyId;
    this.loadProductsForCompany(companyId);
  }

  getSelectedInsuranceCompany() {
    if (!this.selectedInsuranceCompanyId) return null;
    return this.insuranceCompanies.find(company => company.id === this.selectedInsuranceCompanyId) || null;
  }

  private loadProductsForCompany(companyId: number) {
    this.authService.getProductsByCompany(companyId).subscribe({
      next: (products: Product[]) => {
        this.products = products;
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Hiba a termékek betöltése során:', error);
        this.products = [];
        this.showNotification('Hiba történt a termékek betöltése során', 'error');
        this.cdr.markForCheck();
      }
    });
  }

  editProduct(product: Product) {
    const dialogData: ProductDialogData = {
      editingProduct: product,
      formData: {
        insurancecompanyid: product.insurancecompanyid,
        productcode: product.productcode,
        productname: product.productname,
        producttype: product.producttype
      },
      insuranceCompanies: this.insuranceCompanies,
      isSubmitting: false
    };

    const dialogRef = this.dialog.open(ProductDialog, {
      width: '500px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: ProductDialogResult) => {
      if (result?.action === 'save' && result.data) {
        this.updateProduct(product.id, result.data);
      } else if (result?.action === 'delete') {
        this.confirmDeleteProduct(product);
      }
    });
  }

  private updateProduct(productId: number, productData: NewProduct) {
    this.authService.updateProduct(productId, productData).subscribe({
      next: (response: AuthResponse) => {
        this.showNotification('Termék sikeresen frissítve!', 'success');
        // Frissítjük a termékek listáját
        if (this.selectedInsuranceCompanyId) {
          this.loadProductsForCompany(this.selectedInsuranceCompanyId);
        }
      },
      error: (error: any) => {
        console.error('Hiba a termék frissítésekor:', error);
        this.showNotification('Hiba történt a termék frissítése során', 'error');
      }
    });
  }

  private confirmDeleteProduct(product: Product) {
    // A megerősítés már megtörtént a product dialog-ban
    this.authService.deleteProduct(product.id).subscribe({
      next: () => {
        this.showNotification('Termék sikeresen törölve!', 'success');
        // Frissítjük a termékek listáját
        if (this.selectedInsuranceCompanyId) {
          this.loadProductsForCompany(this.selectedInsuranceCompanyId);
        }
      },
      error: (error: any) => {
        console.error('Hiba a termék törlésekor:', error);
        this.showNotification('Hiba történt a termék törlése során', 'error');
      }
    });
  }
}